import { Injectable } from '@angular/core';
import { LoggerService } from '../../../core/services/logger.service';
import { UtilService } from '../../../core/services/util.service';
import { TicketCreationRequestControlService } from 'src/app/shared/services/ticket-creation-request-control.service';
import { CloseTicketByEventProps } from 'src/app/shared/services/cif-datadog';
import { MediaType } from 'src/app/utils/media-type';
import { isDigital } from 'src/app/utils/cxone-utils';
import { CIFDatadogService } from 'src/app/shared/services/cif-datadog';
import { LocalStorageUtils } from 'src/app/data/CXOne/util/LocalStorageUtils';
import { JsonConverterUtils } from 'src/app/data/CXOne/util/JsonConverterUtils';

export type Ticket = { id_interacao: string | null; id_ticket: string | null };

export interface TemplateValuesChannelProvider {
  fieldNegocio: string;
  triggerPlugin: string;
  templateAusente: string;
  templateSessaoPadrao: string;
  templateCreateSession: string;
  retrieveRecordTable: string;
  retrieveRecordCol: string;
}

@Injectable({
  providedIn: 'root',
})
export class DynamicsCivicsV2Service {
  private readonly RECALL_LIMIT = 50;
  private readonly RECALL_DELAY_MS = 2000;
  private readonly tableName: string = 'itau_interacao';
  private readonly retrieveRecordTable =
    'itau_incident_InteracaoId_itau_interacao';
  private readonly retrieveRecordCol = 'incidentid';
  public templateValuesChannelProvider: TemplateValuesChannelProvider;
  private activeContactIds: string[] = [];

  constructor(
    private logger: LoggerService,
    private utilService: UtilService,
    private ticketCreationRequestControlService: TicketCreationRequestControlService,
    private cifDatadogService: CIFDatadogService,
    private localStorageUtils: LocalStorageUtils,
    private jsonConverterUtils: JsonConverterUtils,
  ) {
    this.templateValuesChannelProvider = {
      fieldNegocio: 'itau_name',
      triggerPlugin: 'xs3_st_plugin',
      templateAusente: 'xs3_nice_ausente',
      templateSessaoPadrao: 'xs3_atender_seguros_genesys',
      templateCreateSession: 'xs3_atender_seguros_operacao',
      retrieveRecordTable: 'itau_incident_InteracaoId_itau_interacao',
      retrieveRecordCol: 'incidentid',
    };
  }

  async initialize(cifUrl: string): Promise<void> {
    this.logger.info(`DYNAMICS - inicializando CIF: ${cifUrl}`);
    this.utilService.getIframeEnvironment();
    const script = document.createElement('script');

    script.src = cifUrl;
    script.type = 'text/javascript';
    script.async = true;
    script.setAttribute('data-crmurl', cifUrl);
    script.setAttribute('data-cifid', 'CIFMainLibrary');

    document.body.appendChild(script);

    return new Promise<void>((resolve, reject) => {
      const onCIFInitDone = async (event: Event) => {
        this.logger.info('DYNAMICS - CIF inicializado', event);
        try {
          this.logger.info('DYNAMICS - CIF Iframe setado para 400 pixels');

          await (window as any).Microsoft.CIFramework.setMode(1);

          await (window as any).Microsoft.CIFramework.addHandler(
            'onmodechanged',
            async (eventData: any) => {
              this.logger.info('DYNAMICS - CIF Mode Changed:', eventData);

              await (window as any).Microsoft.CIFramework.setMode(1);
            },
          );

          await (window as any).Microsoft.CIFramework.setWidth(400);
        } catch (error) {
          this.logger.error('DYNAMICS - Erro ao inicializar CIF', error);
          reject(error);
        } finally {
          resolve();
          window.removeEventListener('CIFInitDone', onCIFInitDone);
        }
      };

      window.addEventListener('CIFInitDone', onCIFInitDone);
    });
  }

  public async loadTemplateValues(): Promise<TemplateValuesChannelProvider | null> {
    const channelProviderVariablesRaw = await (
      window as any
    ).Microsoft.CIFramework.getEnvironment();

    if (channelProviderVariablesRaw) {
      const channelProviderVariables = this.jsonConverterUtils.JsonParsed(
        channelProviderVariablesRaw,
      );

      let customParams: any = {};
      if (channelProviderVariables.msdyn_customparams) {
        customParams = Object.fromEntries(
          channelProviderVariables.msdyn_customparams
            .split('&')
            .map((kv: string) => kv.split('=')),
        );
      }

      const templateValues: TemplateValuesChannelProvider = {
        templateCreateSession:
          customParams.templateCreateSession ||
          this.templateValuesChannelProvider.templateCreateSession,
        templateSessaoPadrao:
          customParams.templateSessaoPadrao ||
          this.templateValuesChannelProvider.templateSessaoPadrao,
        templateAusente:
          customParams.templateAusente ||
          this.templateValuesChannelProvider.templateAusente,
        fieldNegocio:
          customParams.fieldNegocio ||
          this.templateValuesChannelProvider.fieldNegocio,
        triggerPlugin:
          customParams.triggerPlugin ||
          this.templateValuesChannelProvider.triggerPlugin,
        retrieveRecordTable:
          customParams.retrieveRecordTable ||
          this.templateValuesChannelProvider.retrieveRecordTable,
        retrieveRecordCol:
          customParams.retrieveRecordCol ||
          this.templateValuesChannelProvider.retrieveRecordCol,
      };

      this.templateValuesChannelProvider = templateValues;

      this.logger.info('DYNAMICS - Template Values Loaded:', templateValues);

      return templateValues;
    } else {
      this.logger.error(
        'Erro ao carregar environment do CIFramework:',
        channelProviderVariablesRaw,
      );
      return null;
    }
  }

  public async cifCreateSession(
    input_template: any,
    mediaType: MediaType | null,
    masterId: string,
  ): Promise<string> {
    let interactionId = null;
    try {
      interactionId = input_template['templateParameters'].id_interacao;
      this.cifDatadogService.openTicket.session.request({
        interactionId,
        template: input_template,
        mediaType,
        masterId,
      });

      const canCreate = await (
        window as any
      ).Microsoft.CIFramework.canCreateSession();
      if (canCreate) {
        const sessionId = await (
          window as any
        ).Microsoft.CIFramework.createSession(input_template);

        this.cifDatadogService.openTicket.session.success({
          sessionId: sessionId,
          interactionId: interactionId,
          mediaType,
          masterId,
        });

        this.logger.info('DYNAMICS - sessionId do createSession', sessionId);
        return sessionId;
      } else {
        this.logger.warn(
          'DYNAMICS - Não é possível criar uma sessão com o template fornecido.',
        );
        throw new Error();
      }
    } catch (error: any) {
      this.cifDatadogService.openTicket.session.fail({
        interactionId,
        template: input_template,
        mediaType,
        masterId,
        error,
      });
      this.logger.error('DYNAMICS - Erro ao criar sessão', error);
      throw error; // Re-throw the error to be caught by the caller
    }
  }

  public async cifCloseSession(
    input_template: string,
    context: any,
    sessionId: string,
    contactId: string,
    mediaType: MediaType,
  ): Promise<void> {
    let idInteracao = null;
    try {
      const updateResult = await (
        window as any
      ).Microsoft.CIFramework.updateContext(context, sessionId);

      const input = {
        templateName: input_template,
        templateParameters: {},
      };

      idInteracao = updateResult?.id_interacao;

      if (!sessionId || !idInteracao) {
        this.logger.warn(
          'CIVICS - sessionId ou idInteracao ausentes, não é possível fechar a sessão.',
        );
        return;
      }

      await (window as any).Microsoft.CIFramework.notifyEvent(input);

      // Chama a função para motivar o encerramento com o id da interação
      const eventInputParameters = {
        sessionId: sessionId,
        correlationId: idInteracao,
      };

      this.logger.info(
        'iframe debug: eventInputParameters',
        eventInputParameters,
      );

      const closeEventLogDetails: CloseTicketByEventProps = {
        sessionId: sessionId,
        interactionId: idInteracao,
        mediaType,
        masterId: contactId,
      };

      this.cifDatadogService.closeTicket.start(
        'fecharSessao',
        closeEventLogDetails,
      );

      await (window as any).Microsoft.CIFramework.raiseEvent(
        'fecharSessao',
        eventInputParameters,
      );

      this.cifDatadogService.closeTicket.success(
        'fecharSessao',
        closeEventLogDetails,
      );

      this.logger.info('CIVICS - sessão fechada com sucesso', sessionId);
    } catch (error) {
      this.logger.error(
        'CIVICS - Erro ao fechar sessão: ',
        error,
        'sessionId:',
        sessionId,
      );

      this.cifDatadogService.closeTicket.fail('fecharSessao', {
        sessionId: sessionId,
        interactionId: idInteracao,
        error,
        mediaType,
        masterId: contactId,
      });
      throw error;
    }
  }

  public async cifCreateTicket(
    data: any,
    retrieveRecordCol: string,
    retrieveRecordTable: string,
    masterId: string,
    mediaType: MediaType,
    uJSON: unknown,
  ): Promise<Ticket> {
    this.logger.info('DYNAMICS - Master ID: ', masterId);
    this.logger.info(
      'DYNAMICS - Parâmetros para criação de ticket: ',
      JSON.stringify(data),
    );
    let retorno: Ticket = {
      id_interacao: null,
      id_ticket: null,
    };

    this.cifDatadogService.openTicket.initialData({
      attachData: data['itau_mst_attachdata'],
      masterId,
      mediaType,
    });

    if (isDigital(mediaType)) {
      this.ticketCreationRequestControlService.clearUpLastTicketCreationRequest();
    }

    this.cifDatadogService.openTicket.request.create({
      attachData: uJSON,
      mediaType,
      masterId: masterId,
    });

    try {
      const result = await (window as any).Microsoft.CIFramework.createRecord(
        this.tableName,
        JSON.stringify(data),
      );

      this.cifDatadogService.openTicket.request.success({
        attachData: data,
        mediaType,
        masterId: masterId,
      });

      this.logger.info(`DYNAMICS - ID Interação criado: ${result}`);
      retorno.id_interacao = this.jsonConverterUtils.JsonParsed(result).id;

      //recuperacao do ID do ticket
      if (retorno.id_ticket == null && retorno.id_interacao != null) {
        retorno.id_ticket = await this.cifRetrieveRecord(
          retorno.id_interacao,
          this.tableName,
          retrieveRecordCol,
          retrieveRecordTable,
          mediaType,
          masterId,
        );
        this.logger.info(`DYNAMICS - Ticket recuperado: ${retorno.id_ticket}`);
      }
    } catch (error) {
      this.cifDatadogService.openTicket.request.fail({
        error,
        attachData: data,
        mediaType,
        masterId: masterId,
      });

      throw new Error(
        `Erro ao criar interação ou recuperar ticket: ${error instanceof Error ? error.message : error}`,
      );
    }
    return retorno;
  }

  private async cifRetrieveRecord(
    interactionId: string,
    tableName: string,
    retrieveRecordCol: string,
    retrieveRecordTable: string,
    mediaType: MediaType,
    masterId: string,
  ): Promise<string | null> {
    let idTicket = null;

    this.cifDatadogService.openTicket.retrieve.start({
      interactionId,
      mediaType,
      masterId,
    });

    for (let attempt = 0; attempt < this.RECALL_LIMIT; attempt++) {
      await this.delay(this.RECALL_DELAY_MS);

      const options = `?$select=itau_protocolo_interacao&$expand=${retrieveRecordTable}($select=${retrieveRecordCol})`;
      try {
        const result = await (
          window as any
        ).Microsoft.CIFramework.retrieveRecord(
          tableName,
          interactionId,
          options,
        );
        this.logger.info(`DYNAMICS - result retrieve: ${result}`);

        const interaction = this.jsonConverterUtils.JsonParsed(result);

        this.logger.info(`DYNAMICS - interaction: ${interaction}`);

        if (interaction && interaction[retrieveRecordTable]?.[0] != null) {
          idTicket = interaction[retrieveRecordTable][0][retrieveRecordCol];

          this.logger.info(
            `DYNAMICS - Ticket ID encontrado na tentativa ${attempt + 1}: ${idTicket}`,
          );
          this.cifDatadogService.openTicket.retrieve.success({
            interactionId,
            mediaType,
            masterId,
          });
          break;
        }
      } catch (error) {
        throw new Error(
          `Erro ao recuperar ticket na tentativa ${attempt + 1}: ${error instanceof Error ? error.message : error}`,
        );
      }
    }

    if (!idTicket) {
      this.logger.warn(
        `DYNAMICS - Limite de tentativas (${this.RECALL_LIMIT}) atingido ao recuperar o Ticket ID para a interação ${interactionId}`,
      );
      this.cifDatadogService.openTicket.retrieve.fail({
        interactionId,
        mediaType,
        masterId,
      });
    }

    return idTicket;
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
